import type { GridRowSpacingParams } from '@mui/x-data-grid';
import type { Theme } from '@mui/material/styles';
import palette from './palette';

const datagrid = {
  MuiDataGrid: {
    defaultProps: {
      columnHeaderHeight: 56,
      rowHeight: 46,
      rowSpacingType: 'margin',
      getRowSpacing: ({ isFirstVisible }: GridRowSpacingParams) => ({
        top: isFirstVisible ? 0 : 8,
        bottom: 0,
      }),
    },
    styleOverrides: {
      root: {
        '--DataGrid-rowBorderColor': palette.grey[200],
        '--unstable_DataGrid-radius': '6px',
        backgroundColor: 'transparent',
        border: 0,
        fontSize: '16px',
        width: '100%',
        '& .MuiDataGrid-columnSeparator': {
          display: 'none',
        },
      },
      columnHeaders: {
        border: `1px solid ${palette.grey[200]}`,
        borderBottom: 0,
        '& .MuiDataGrid-row--borderBottom': {
          paddingBottom: 8,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: -1,
            right: -1,
            bottom: 0,
            height: 8,
            backgroundColor: palette.grey[150],
          },
        },
      },
      columnHeader: ({ theme }: { theme: Theme }) => ({
        '--DataGrid-t-header-background-base': 'transparent',
        transition: theme.transitions.create(
          ['background-color']
        ),
        '&:hover': {
          backgroundColor: palette.grey[100],
        },
      }),
      scrollbarFiller: {
        '--DataGrid-t-header-background-base': 'transparent',
      },
      row: ({ theme }: { theme: Theme }) => ({
        // Cells with a gradient overlay fade their text into this color.
        '--DataGrid-row-bg': palette.common.white,
        backgroundColor: 'var(--DataGrid-row-bg)',
        border: `1px solid ${palette.grey[200]}`,
        // Rows react to clicks (expand, open on double-click); keep those
        // clicks from selecting text.
        userSelect: 'none',
        transition: theme.transitions.create(
          ['background-color', 'border-color', 'color']
        ),
        '&.MuiDataGrid-row--lastVisible': {
          borderBottomLeftRadius: 'var(--unstable_DataGrid-radius)',
          borderBottomRightRadius: 'var(--unstable_DataGrid-radius)',
        },
        '& .MuiSvgIcon-colorAction': {
          transition: theme.transitions.create(['color']),
        },
        // A row gutter (see shared/ui/DataGrid) is part of the row element but
        // looks like it is outside the table, so hovering it is not a row
        // hover.
        '&:not(.MuiDataGrid-row--details):not(.DataGrid-row--editing):not(:has(.DataGrid-gutterCell:hover)):hover':
          {
            '--DataGrid-row-bg': palette.common.black,
            backgroundColor: 'var(--DataGrid-row-bg)',
            color: palette.common.white,
            '& .MuiIconButton-root, & .MuiSvgIcon-colorAction': {
              color: palette.common.white,
            },
            '& .MuiIconButton-colorSecondary:not(:hover, :hover:active, :focus-visible)': {
              backgroundColor: palette.common.white,
            },
          },
        // A click on the row expands or collapses it.
        '&.MuiDataGrid-row--expandable': {
          cursor: 'pointer',
        },
        // A details row continues the row above it: no gap or border between
        // them, and its content can be selected and copied.
        '&.MuiDataGrid-row--details': {
          borderTop: 0,
          // Beats the inline margin set by getRowSpacing.
          marginTop: '-1px !important',
          userSelect: 'text',
          '&:hover': {
            backgroundColor: palette.common.white,
          },
        },
        // Finished items (completed tasks, resolved alerts).
        '&.MuiDataGrid-row--muted': {
          '& .MuiDataGrid-cell': {
            color: palette.text.secondary,
          },
          '& .MuiChip-root': {
            color: 'inherit',
          },
        },
        // A row whose inputs are being edited in place (EDITING_ROW_CLASS of
        // shared/ui/ExpandableEditableDataGrid), with its details: light red,
        // also hovered (after the hover rules, which it overrides).
        '&.DataGrid-row--editing, &.DataGrid-row--editing:hover': {
          '--DataGrid-row-bg': palette.red[100],
          backgroundColor: 'var(--DataGrid-row-bg)',
        },
        '&.MuiDataGrid-row--inactive': {
          color: palette.text.disabled,
          "&:hover": {
            color: `${palette.text.disabled} !important`,
          },
          '& img': {
            filter: 'grayscale(1)',
          },
        }
      }),
      cell: ({ theme }: { theme: Theme }) => ({
        border: 0,
        height: 'calc(var(--height) - 2px)',
        lineHeight: 'calc(var(--height) - 2px)',
        '&:focus, &:focus-within': {
          outline: 'none',
        },
        '&.MuiDataGrid-cell--gradientOverlay': {
          position: 'relative',
          textOverflow: 'clip',
          '&::after': {
            content: '""',
            backgroundColor: 'var(--DataGrid-row-bg)',
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 93,
            zIndex: 0,
            transition: theme.transitions.create(['background-color']),
            maskImage: 'linear-gradient(90deg, rgba(0, 0, 0, 0) -20.45%, rgba(0, 0, 0, 1) 100%)',
            maskRepeat: 'no-repeat',
            maskSize: '100% 100%',
          },
        },
      }),
    },
  },
};

export default datagrid;
